import { FC } from 'react';
import { Modal, RadioGroup, TextArea, Checkbox, CodeDisplay, Button } from '@aidkitorg/component-library';
import { safeParse } from '../../../../../../Util';
import { useLocalizedStrings } from '../../../../../../Localization';
import { DebugMessageModalProps, HideMessageModalProps, DeleteAttachmentsModalProps, ConfirmDeleteModalProps } from '../shared/types';
import { getHideMessageReasonOptions } from '../shared/constants';

// Debug Message Modal
export const DebugMessageModal: FC<DebugMessageModalProps> = ({ showDebugMessage, onClose }) => {
  const L = useLocalizedStrings();

  if (!showDebugMessage) return null;

  return (
    <Modal size="lg" title={L.applicant.comms.debug_message} open={!!showDebugMessage} onClose={onClose}>
      <div className="max-h-[70vh] overflow-auto">
        <CodeDisplay
          code={JSON.stringify({
            ...showDebugMessage,
            message: showDebugMessage.message ? {
              ...showDebugMessage.message,
              metadata: safeParse(showDebugMessage.message.metadata || '{}')
            } : undefined
          }, null, 2)}
          language="json"
          expandable={true}
          copyable={true}
        />
      </div>
    </Modal>
  );
};

// Hide Message Modal
export const HideMessageModal: FC<HideMessageModalProps> = ({
  showHideMessage,
  hideMessageReason,
  hideMessageDetails,
  shouldHideAttachmentOnly,
  onClose,
  onReasonChange,
  onDetailsChange,
  onAttachmentOnlyChange,
  onConfirm,
}) => {
  const L = useLocalizedStrings();

  if (!showHideMessage) return null;

  const hideMessageReasonOptions = getHideMessageReasonOptions(L);

  return (
    <Modal title={L.applicant.comms.hide_message} open={!!showHideMessage} onClose={onClose}>
      <div className="space-y-4">
        <RadioGroup
          label={L.applicant.comms.hide_message_reason}
          options={hideMessageReasonOptions}
          value={hideMessageReason}
          onChange={(value) => onReasonChange(value as any)}
        />

        <TextArea
          label={L.applicant.comms.hide_message_details}
          placeholder={L.applicant.comms.hide_message_details}
          value={hideMessageDetails}
          onChange={(e) => onDetailsChange(e.target.value)}
        />

        {safeParse(showHideMessage.message?.metadata || '{}').attachments?.length > 0 && (
          <Checkbox
            label={L.applicant.comms.hide_attachment_only}
            checked={shouldHideAttachmentOnly}
            description={shouldHideAttachmentOnly ? L.applicant.comms.hide_attachment_only_description : undefined}
            onChange={onAttachmentOnlyChange}
          />
        )}

        <div className="flex justify-end gap-x-3">
          <Button size="md" variant="secondary" onClick={onClose}>
            {L.apply.cancel}
          </Button>
          <Button
            size="md"
            variant="primary"
            isDisabled={!hideMessageReason}
            onClick={onConfirm}
          >
            {L.applicant.comms.hide_message}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Delete Attachments Modal
export const DeleteAttachmentsModal: FC<DeleteAttachmentsModalProps> = ({
  showDeleteAttachments,
  deleteAttachmentsReason,
  onClose,
  onReasonChange,
  onConfirm,
}) => {
  const L = useLocalizedStrings();

  if (!showDeleteAttachments) return null;

  return (
    <Modal title={L.applicant.comms.delete_attachments || 'Delete Attachments'} open={showDeleteAttachments} onClose={onClose}>
      <div className="space-y-4">
        <p>{L.applicant.comms.delete_attachments_description || 'Are you sure you want to delete the attachments from this message? This action cannot be undone.'}</p>
        <TextArea
          label={L.applicant.comms.delete_artifact_reason || 'Reason for deletion'}
          placeholder={L.applicant.comms.delete_artifact_reason || 'Please provide a reason...'}
          value={deleteAttachmentsReason}
          onChange={(e) => onReasonChange(e.target.value)}
          required
        />

        <div className="flex justify-end gap-x-3">
          <Button size="md" variant="secondary" onClick={onClose}>
            {L.apply.cancel}
          </Button>
          <Button
            size="md"
            variant="primary"
            isDisabled={!deleteAttachmentsReason.trim()}
            onClick={onConfirm}
          >
            {L.applicant.comms.delete_attachments || 'Delete Attachments'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Confirm Delete Modal
export const ConfirmDeleteModal: FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  message,
}) => {
  const L = useLocalizedStrings();

  if (!isOpen) return null;

  return (
    <Modal title={L.applicant.comms.delete_message} open={isOpen} onClose={onClose}>
      <div className="space-y-4">
        <p>{message}</p>

        <div className="flex justify-end gap-x-3">
          <Button size="md" variant="secondary" onClick={onClose}>
            {L.apply.cancel}
          </Button>
          <Button
            size="md"
            variant="primary"
            onClick={onConfirm}
          >
            {L.applicant.comms.delete_message}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
